import { isNil } from 'lodash-es'

import { createOverlay } from './create-overlay'
import { getElement } from './element'
import { type Map, mapManager, TCoordinates } from '../../map-manager'

export type BaseOverlayOptions = {
  position: naver.maps.LatLng | TCoordinates
  map: Map
  content: string
  zIndex?: number
  nextZoomLevel?: number | null
  onClick?: () => void
  onOutsideClick?: () => void
}

export interface BaseOverlay extends naver.maps.OverlayView {
  _element: HTMLDivElement
  _position: naver.maps.LatLng
  _nextZoomLevel?: number | null
  _onClick?: () => void
  _cleanUp?: () => void
  _morph(): void
  id: string
  setPosition(position: naver.maps.LatLng | TCoordinates): void
  getPosition(): naver.maps.LatLng
  addEventListener(options: {
    eventName: string
    callback: (event: unknown) => void
  }): void
}

export const getOverlayId = (
  position: naver.maps.LatLng | TCoordinates,
): string => {
  if (position instanceof naver.maps.LatLng) {
    return `${position.lat()}-${position.lng()}`
  }

  return `${position.lat}-${position.lng}`
}

export const createBaseOverlay = () => {
  const Overlay = createOverlay<BaseOverlay, BaseOverlayOptions>({
    initialize,
  })

  function initialize(this: BaseOverlay, options: BaseOverlayOptions) {
    this.id = getOverlayId(options.position)
    this._element = getElement({
      content: options.content,
      zIndex: options.zIndex,
    })
    this._nextZoomLevel = options.nextZoomLevel
    this.setPosition(options.position)
  }

  Overlay.prototype.onAdd = function () {
    const { overlayLayer } = this.getPanes()
    overlayLayer.appendChild(this._element)

    const handleClick = (event: MouseEvent) => {
      const isDragging = mapManager.getIsDragging()

      if (isDragging) return

      event.stopPropagation()

      this._onClick?.()
      this._morph()
    }

    const handleTouchEnd = (event: TouchEvent) => {
      const isDragging = mapManager.getIsDragging()
      const isPinching = mapManager.getIsPinching()

      if (isDragging || isPinching) return

      event.preventDefault()
      event.stopPropagation()

      this._onClick?.()
      this._morph()
    }

    this._element.addEventListener('click', handleClick)
    this._element.addEventListener('touchend', handleTouchEnd)

    this._cleanUp = () => {
      this._element.removeEventListener('click', handleClick)
      this._element.removeEventListener('touchend', handleTouchEnd)
    }
  }

  Overlay.prototype.draw = function () {
    if (!this.getMap()) return

    const projection = this.getProjection()
    const position = this.getPosition()
    const pixelPosition = projection.fromCoordToOffset(position)

    this._element.style.left = `${pixelPosition.x}px`
    this._element.style.top = `${pixelPosition.y}px`
  }

  Overlay.prototype.onRemove = function () {
    this._element.parentNode?.removeChild(this._element)
    this._cleanUp?.()
  }

  Overlay.prototype.setPosition = function (
    position: naver.maps.LatLng | TCoordinates,
  ) {
    if (position instanceof naver.maps.LatLng) {
      this._position = position
    } else {
      this._position = new naver.maps.LatLng(position.lat, position.lng)
    }

    this.draw()
  }

  Overlay.prototype.getPosition = function (): naver.maps.LatLng {
    return this._position
  }

  Overlay.prototype._morph = function () {
    mapManager.withMap(map => {
      if (isNil(this._nextZoomLevel)) return

      if (this._nextZoomLevel <= map.getZoom()) return

      map.morph(this.getPosition(), this._nextZoomLevel, {
        duration: 300,
        easing: 'easeOutCubic',
      })
    })
  }

  return Overlay
}
